import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaPlus, FaEdit, FaTimes } from 'react-icons/fa';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import IconButton from '../common/IconButton';
import useApi from '../../hooks/useApi';
import { toast } from 'react-toastify';
import 'animate.css';

function MemberForm({ member = null, onSuccess = () => {}, onCancel = () => {} }) {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    gender: member?.gender || '',
    bloodGroup: member?.bloodGroup || '',
    age: member?.age || '',
    birthdate: member?.birthdate || '',
    mobileNo: member?.mobileNo || '',
    relation: member?.relation || '',
  });
  const [errors, setErrors] = useState({});
  const { post, put } = useApi();

  const isEditing = !!member;
  const isSelfRelation = formData.relation === 'SELF';

  // Calculate age from birthdate
  const calculateAge = (birthdate) => {
    if (!birthdate) return '';
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : '';
  };

  useEffect(() => {
    setFormData({
      name: member?.name || '',
      gender: member?.gender || '',
      bloodGroup: member?.bloodGroup || '',
      age: member?.age || calculateAge(member?.birthdate) || '',
      birthdate: member?.birthdate || '',
      mobileNo: member?.mobileNo || '',
      relation: member?.relation || '',
    });
    setErrors(
      validateForm({
        name: member?.name || '',
        age: member?.age || calculateAge(member?.birthdate) || '',
        mobileNo: member?.mobileNo || '',
        birthdate: member?.birthdate || '',
      })
    );
  }, [member]);

  const validateForm = (data) => {
    const newErrors = {};
    if (!data.name) {
      newErrors.name = 'Name is required.';
    }
    if (data.age && (isNaN(data.age) || data.age < 0 || data.age > 150)) {
      newErrors.age = 'Age must be a valid number between 0 and 150.';
    }
    if (data.mobileNo && !/^\d{10}$/.test(data.mobileNo)) {
      newErrors.mobileNo = 'Mobile number must be exactly 10 digits.';
    }
    if (data.birthdate) {
      const birthDate = new Date(data.birthdate);
      const today = new Date();
      if (birthDate > today) {
        newErrors.birthdate = 'Birthdate cannot be in the future.';
      }
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Prevent changes to read-only fields for SELF members
    if (isEditing && isSelfRelation && ['name', 'mobileNo', 'relation'].includes(name)) {
      return; // Ignore changes to these fields
    }
    const updatedFormData = { ...formData, [name]: value };

    // Auto-calculate age when birthdate changes
    if (name === 'birthdate') {
      updatedFormData.age = calculateAge(value);
    }

    setFormData(updatedFormData);
    const validationErrors = validateForm(updatedFormData);
    setErrors(validationErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      // Send all fields in the payload
      const payload = { ...formData };
      const response = member
        ? await put(`/api/members/${member.id}`, payload)
        : await post('/api/members', payload);
      onSuccess(response);
      if (!member) {
        setFormData({
          name: '',
          gender: '',
          bloodGroup: '',
          age: '',
          birthdate: '',
          mobileNo: '',
          relation: '',
        });
        setErrors({});
      }
      toast.success(member ? 'Member updated.' : 'Member added.');
    } catch (err) {
      console.error('MemberForm - Submit error:', err);
      // Extract error message from backend response
      const errorMessage = err.response?.data?.message || 'Failed to save member.';
      setErrors({ general: errorMessage });
      toast.error(errorMessage);
    }
  };

  return (
    <div className="container py-3 animate__animated animate__fadeIn">
      <div className="card shadow mb-4">
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3">{member ? 'Update Member' : 'Add Member'}</h5>
          {isEditing && isSelfRelation && (
            <Alert
              type="info"
              message="Name, Mobile Number, and Relation cannot be edited for SELF members."
            />
          )}
          {errors.general && <Alert type="danger" message={errors.general} />}
          <form onSubmit={handleSubmit}>
            <style>
              {`
                .read-only-input {
                  background-color: #f8f9fa;
                  border: 1px solid #dee2e6;
                  color: #6c757d;
                  cursor: not-allowed;
                  pointer-events: none; /* Prevent interaction */
                }
                .read-only-input:focus {
                  outline: none;
                  box-shadow: none;
                }
              `}
            </style>
            <div className="d-flex flex-wrap gap-3">
              <div className="form-group flex-fill" style={{ minWidth: '250px' }}>
                <FormInput
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={isEditing && isSelfRelation ? 'Name cannot be edited' : 'Enter name'}
                  error={errors.name}
                  disabled={isEditing && isSelfRelation}
                  className={isEditing && isSelfRelation ? 'read-only-input' : ''}
                />
              </div>
              <div className="form-group flex-fill" style={{ minWidth: '250px' }}>
                <label className="form-label">Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Select Gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="form-group flex-fill" style={{ minWidth: '250px' }}>
                <FormInput
                  label="Blood Group"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                  placeholder="e.g., A_POSITIVE"
                  list="bloodGroups"
                />
                <datalist id="bloodGroups">
                  <option value="A_POSITIVE" />
                  <option value="A_NEGATIVE" />
                  <option value="B_POSITIVE" />
                  <option value="B_NEGATIVE" />
                  <option value="AB_POSITIVE" />
                  <option value="AB_NEGATIVE" />
                  <option value="O_POSITIVE" />
                  <option value="O_NEGATIVE" />
                </datalist>
              </div>
              <div className="form-group flex-fill" style={{ minWidth: '250px' }}>
                <FormInput
                  label="Birthdate"
                  name="birthdate"
                  type="date"
                  value={formData.birthdate}
                  onChange={handleChange}
                  placeholder="Select birthdate"
                  error={errors.birthdate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group flex-fill" style={{ minWidth: '250px' }}>
                <FormInput
                  label="Age"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="Enter age"
                  error={errors.age}
                  readOnly={!!formData.birthdate}
                />
              </div>
              <div className="form-group flex-fill" style={{ minWidth: '250px' }}>
                <FormInput
                  label="Mobile Number"
                  name="mobileNo"
                  value={formData.mobileNo}
                  onChange={handleChange}
                  placeholder={isEditing && isSelfRelation ? 'Mobile number cannot be edited' : 'Enter 10-digit mobile number'}
                  error={errors.mobileNo}
                  disabled={isEditing && isSelfRelation}
                  className={isEditing && isSelfRelation ? 'read-only-input' : ''}
                />
              </div>
              <div className="form-group flex-fill" style={{ minWidth: '250px' }}>
                <FormInput
                  label="Relation"
                  name="relation"
                  value={formData.relation}
                  onChange={handleChange}
                  placeholder={isEditing && isSelfRelation ? 'Relation cannot be edited' : 'e.g., Spouse, Child, Parent'}
                  list="relations"
                  error={errors.relation}
                  disabled={isEditing && isSelfRelation}
                  className={isEditing && isSelfRelation ? 'read-only-input' : ''}
                />
                <datalist id="relations">
                  <option value="SELF" />
                  <option value="SPOUSE" />
                  <option value="CHILD" />
                  <option value="PARENT" />
                  <option value="SIBLING" />
                  <option value="OTHER" />
                </datalist>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 mt-3 justify-content-center">
              <Button
                type="submit"
                variant="primary"
                className="flex-fill"
                style={{ maxWidth: '200px' }}
                disabled={Object.keys(errors).length > 0 || !formData.name}
              >
                {member ? <FaEdit /> : <FaPlus />} {member ? 'Update' : 'Add'}
              </Button>
              <IconButton
                icon={<FaTimes />}
                tooltip="Cancel"
                variant="secondary"
                onClick={() => {
                  setFormData({
                    name: '',
                    gender: '',
                    bloodGroup: '',
                    age: '',
                    birthdate: '',
                    mobileNo: '',
                    relation: '',
                  });
                  setErrors({});
                  onCancel();
                }}
                className="flex-fill"
                style={{ maxWidth: '200px' }}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

MemberForm.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    gender: PropTypes.string,
    bloodGroup: PropTypes.string,
    age: PropTypes.number,
    birthdate: PropTypes.string,
    mobileNo: PropTypes.string,
    relation: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default MemberForm;